import React, { createContext, useState, useContext, useEffect } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";

interface User {
  sessionId: string; // Save only sessionId, which represents the document ID of the logged-in user
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  clearUser: () => void;
  isAuthenticated: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(null);

  // Save user sessionId to AsyncStorage and state
  const setUser = async (newUser: User | null) => {
    if (newUser) {
      await AsyncStorage.setItem("AZP", JSON.stringify(newUser));
    } else {
      await AsyncStorage.removeItem("AZP");
    }
    setUserState(newUser);
  };

  // Clear user session
  const clearUser = async () => {
    await AsyncStorage.removeItem("AZP");
    setUserState(null);
  };

  // Load user from AsyncStorage during initialization
  const loadUserFromStorage = async () => {
    const storedUser = await AsyncStorage.getItem("AZP");
    if (storedUser) {
      setUserState(JSON.parse(storedUser));
    }
  };

  useEffect(() => {
    loadUserFromStorage();
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        clearUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to access UserContext
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
